import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { SectionView } from '@views';
import Logger from '@utils/logger';
import { DateTime } from 'luxon';

type NavigationEligibilityContextType = {
  nextEnabled: boolean;
  prevEnabled: boolean;
  finishVisible: boolean;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  triggerEligibility: () => void;
  setSectionRef: (section: SectionView) => void;
};

const NavigationEligibilityContext = createContext<NavigationEligibilityContextType | null>(null);

export const useNavigationEligibility = () => {
  const ctx = useContext(NavigationEligibilityContext);
  if (!ctx) throw new Error('useNavigationEligibility must be used within a NavigationEligibilityProvider');
  return ctx;
};

export const NavigationEligibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const sectionRef = useRef<SectionView | null>(null);
  const setSectionRef = (section: SectionView) => {
    if (sectionRef.current && sectionRef.current.id !== section.id) {
      setActiveStep(0);
    }
    sectionRef.current = section;
    evaluateEligibility(); // optional: evaluate immediately
  };
  const [activeStep, setActiveStep] = useState(0);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [finishVisible, setFinishVisible] = useState(false);
  const [prevEnabled, setPrevEnabled] = useState(true); // Default to true or whatever your rule is
  const evaluateEligibility = useCallback(() => {
    const section = sectionRef.current;
    if (!section || !section.questions || section.questions.length === 0) return;
    if (activeStep < 0 || activeStep >= section.questions.length) {
      return;
    }
    const hasResponse = section.questions[activeStep].responses.some((r) => !!r.textResponse?.trim());

    setPrevEnabled(activeStep > 0);
    Logger.debug(`[${DateTime.now()}] hasResponse: ${hasResponse}`);
    Logger.debug(
      `[${DateTime.now()}] Setting finishVisible = ${hasResponse && activeStep === section.questions.length - 1}`
    );
    setFinishVisible(hasResponse && activeStep === section.questions.length - 1);
    setNextEnabled(hasResponse);
  }, [activeStep]);

  return (
    <NavigationEligibilityContext.Provider
      value={{
        nextEnabled,
        prevEnabled,
        finishVisible,
        setCurrentStep: setActiveStep,
        triggerEligibility: evaluateEligibility,
        setSectionRef,
      }}
    >
      {children}
    </NavigationEligibilityContext.Provider>
  );
};
